import { getTableColumns } from '@op/pages/Overview/KeyMetrics/MentionsSentiment/MetricsChart/utils';
import { DownloadConfigInfoType, DownloadParamsType } from '../types';
import {
  brandHealthRenderHelper,
  googleTrendsRenderHelper,
  languageRenderHelper,
  sentimentNumber,
  tryTransNumber,
} from '../renderHelper/index';
import { getOverviewCompetitorTimeline } from '@op/services';
import { dealWithMentionsSentimentChart } from '@op/pages/Overview/CompareKeyMetrics/utils';
import { formatTime } from '../renderHelper/time';
import { channelInfo } from '@op/components/ChannelLabel';
import { dealWithOthersName } from '@op/utils/languageValue';

const excelOtherProps: any = {
  brand_health: {
    renderHelper: [brandHealthRenderHelper],
  },
  avg_sentiment: {
    renderHelper: [sentimentNumber],
  },
};

export const KeyMetricsMentionsSentiment: DownloadConfigInfoType = {
  columns: function columnsList({
    filter,
    auxiliaryParams,
  }: DownloadParamsType) {
    const { isRatingRatio, ...otherInfo } = auxiliaryParams;
    const columns = getTableColumns({ ...otherInfo, filter });
    // console.log(columns, '--------------');
    return columns.map(item => {
      const { key } = item;
      let obj = excelOtherProps[key] || {};
      if (
        isRatingRatio &&
        [
          'mobile_rating_1_mentions',
          'mobile_rating_2_mentions',
          'mobile_rating_3_mentions',
          'mobile_rating_4_mentions',
          'mobile_rating_5_mentions',
        ].includes(key)
      ) {
        obj = { numFmt: '0.00%' };
      }
      return {
        ...item,
        ...obj,
      };
    });
  },
};

export const KeyMetricsCommunityLeftChart: DownloadConfigInfoType = {
  columns: function columnsList({ auxiliaryParams }: DownloadParamsType) {
    const { columns = [], isPercent, byType } = auxiliaryParams || {};

    return [
      {
        key: 'time',
        header: 'Date',
      },
      ...columns.map((item: any) => {
        if (isPercent) return { ...item, numFmt: ['0.00%'] };

        if (byType === 'by_channel') {
          const { name } = channelInfo(item.key);

          return {
            ...item,
            header: name,
          };
        }
        if (byType === 'by_language') {
          const header = dealWithOthersName(item.key, 'language');

          return {
            ...item,
            header,
          };
        }
        return item;
      }),
    ];
  },
};

export const KeyMetricsGameStoreRating: DownloadConfigInfoType = {
  columns: () => [
    {
      key: 'time',
      header: 'Date',
    },
    {
      key: 'excelName',
      header: 'Rating',
      renderHelper: [tryTransNumber],
    },
    {
      key: 'excelVal',
      header: 'Rating Reviews',
      numFmt: ['0'],
    },
    {
      key: 'excelShare',
      header: 'Rating%',
      numFmt: ['0.00%'],
    },
  ],
};

export const KeyMetricsGameStoreSteam: DownloadConfigInfoType = {
  columns: ({ auxiliaryParams }) => {
    const { tableColumns } = auxiliaryParams;
    const list = tableColumns.map(({ text, key, ...otherInfo }: any) => {
      return {
        header: text,
        key,
        ...otherInfo,
      };
    });

    return [{ header: 'Date', key: 'time' }, ...list];
  },
};

export const KeyMetricsOfficialAndEarned: DownloadConfigInfoType = {
  columns({ auxiliaryParams }: DownloadParamsType) {
    const { columns, isPercent } = auxiliaryParams || {};
    return [
      {
        key: 'time',
        header: 'Date',
      },
      ...(columns.map((val: any) => ({
        ...val,
        header: val?.text || val?.header,
        numFmt: isPercent ? ['0.00%'] : ['0'],
      })) || []),
    ];
  },
};

export const KeyMetricsGoogleTrends: DownloadConfigInfoType = {
  columns: ({ filter }) => {
    return [
      {
        header: 'Date',
        key: 'time',
        renderHelper: [formatTime(filter)],
      },
      {
        header: 'Google Trends',
        key: 'value',
        renderHelper: [googleTrendsRenderHelper],
      },
    ];
  },
  downloadFunc: getOverviewCompetitorTimeline,
  dealWithDataFunc: ({ data, filter }: DownloadParamsType) => {
    const { tableData } = dealWithMentionsSentimentChart(data, filter);
    return tableData;
  },
};

export const KeyMetricsLanguage: DownloadConfigInfoType = {
  columns: ({ auxiliaryParams }) => {
    const { columnsConfig } = auxiliaryParams;
    const defaultColumns = [
      {
        key: 'name',
        header: 'Language',
        renderHelper: [languageRenderHelper],
      },
      {
        key: 'count',
        header: 'Total Mentions',
        numFmt: ['0'],
      },
      {
        key: 'count_change',
        header: 'Total Mentions Change%',
        numFmt: ['0.00%'],
      },
      {
        key: 'avg_sentiment',
        header: 'Sentiment',
        numFmt: ['0.00'],
      },
      {
        key: 'positive',
        header: 'Positive%',
        numFmt: ['0.00%'],
      },
      {
        key: 'neutral',
        header: 'Neutral%',
        numFmt: ['0.00%'],
      },
      {
        key: 'negative',
        header: 'Negative%',
        numFmt: ['0.00%'],
      },
    ];

    if (Array.isArray(columnsConfig) && columnsConfig.length > 0) {
      return columnsConfig.map(val => {
        const renderHelper: any[] = [];
        const { header, key } = val;
        if (header?.includes('Language')) {
          renderHelper.push(languageRenderHelper);
        } else if (header?.includes('Channel'))
          renderHelper.push((item: any) => channelInfo(item?.name).name);

        if (key?.includes('+')) {
          const splitKey = key.split('+');
          renderHelper.push((item: any) =>
            splitKey.reduce(
              (sum: number, _: any, index: number) =>
                sum + (item?.[splitKey[index]] || 0),
              0,
            ),
          );
        }

        return {
          ...val,
          renderHelper,
        };
      });
    }

    return defaultColumns;
  },
};

export const KeyMetricsChannel: DownloadConfigInfoType = {
  columns: () => [
    {
      key: 'name',
      header: 'Channel',
      renderHelper: [(item: any) => channelInfo(item?.name).name],
    },
    {
      key: 'count',
      header: 'Total Mentions',
      numFmt: ['0'],
    },
    {
      key: 'count_change',
      header: 'Total Mentions Change%',
      numFmt: ['0.00%'],
    },
    {
      key: 'avg_sentiment',
      header: 'Sentiment',
      numFmt: ['0.00'],
    },
    {
      key: 'positive',
      header: 'Positive%',
      numFmt: ['0.00%'],
    },
    {
      key: 'neutral',
      header: 'Neutral%',
      numFmt: ['0.00%'],
    },
    {
      key: 'negative',
      header: 'Negative%',
      numFmt: ['0.00%'],
    },
  ],
};
